import type { DasOverrides } from '@makemydeal/dr-dash-types';
import {
    DasOverridesCompare,
    type DasOverridesFlagsTypes,
    type DasPayment,
    type DasState,
    type InfoMessage,
    type StateTree
} from '@makemydeal/dr-dash-types';
import type { GenericObject } from '@makemydeal/dr-platform-types';
import { utils as offerReduxUtils } from '@makemydeal/dr-offer-redux';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { createSelector } from 'reselect';
import { DAS_OVERRIDES_FIELDS } from '../constants';
import { isAllFeesCapitalized, isTermAllFeesCapitalized, updateFeesWithCapIndicator } from '../utils/feesUtils';
import { isTermAllTaxCapitalized, setManualTaxes } from '../utils/taxUtils';
import {
    getAppliedIncentivesTotalForNonDealerCash,
    getCapCostReduction,
    getCapCostReductionTax,
    getCapReductionTaxCapitalizedIndicator,
    getCashDueFromCustomer,
    getDownPayment,
    getCurrentOfferType,
    getDiaDueAtSigning,
    getEquityAllocationMethod,
    getFeesOverride,
    getFirstMonthPayment,
    getFirstPaymentCapitalized,
    getIsTargetOutOfPocket,
    getPaidAtSigning,
    getPriorLoanBalance,
    getUpfrontAddonsTotal,
    getUpfrontFeesTotal,
    getUpfrontTaxTotal,
    isTaxCapped
} from './offerRedux';
import { isAnyMainPaymentInFlight, isPaymentCallPending } from './BaseDealSelectors';
import { getIsFirstPaymentCapitalized } from './calculationDetailsSelectors';
import { getMonthlyPayment, getSecurityDepositOverrideWithFallback } from './offerSelectors';
import { isAwaitingFreshPaymentResponse, isCalculatingPayment } from './payment';
import { getNetTradeInAllowanceValue } from './trade';

export const getDueAtEditOptions = (state: StateTree): DasState => {
    return state.dueAtSigning || ({} as DasState);
};

export const getIsSlideOutOpen = (state: StateTree): boolean => {
    const dueAtSigning = getDueAtEditOptions(state);
    return dueAtSigning.isSlideOutOpen ?? false;
};

export const getIsCalculating = (state: StateTree): boolean => {
    const dueAtSigning = getDueAtEditOptions(state);
    return dueAtSigning.isCalculating ?? false;
};

export const getIsDasLocked = (state: StateTree): boolean => {
    if (getIsCalculating(state)) {
        return true;
    }
    if (!featureToggleSelectors.isDasPaymentRefreshEnabled(state)) {
        return false;
    }
    return (
        isCalculatingPayment(state) ||
        isAwaitingFreshPaymentResponse(state) ||
        isAnyMainPaymentInFlight(state) ||
        isPaymentCallPending(state)
    );
};

export const getIsApplying = (state: StateTree): boolean => {
    const dueAtSigning = getDueAtEditOptions(state);
    return dueAtSigning.isApplying ?? false;
};

export const getIsApplied = (state: StateTree): boolean => {
    const dueAtSigning = getDueAtEditOptions(state);
    return dueAtSigning.isApplied ?? false;
};

export const getDasOverrides = (state: StateTree): DasOverrides => {
    const dueAtEditItems = getDueAtEditOptions(state);
    return dueAtEditItems.overrides;
};

export const getTargetCashAmountPaidOverride = (state: StateTree): boolean | undefined => {
    const overrides = getDasOverrides(state);
    return overrides?.targetCashAmountPaid;
};

export const getFirstPaymentCapitalizedOverride = (state: StateTree): boolean | undefined => {
    const overrides = getDasOverrides(state);
    return overrides?.firstPaymentCapitalized;
};

export const getEquityAllocationMethodValue = (state: StateTree): string | undefined => {
    const dueAtEditItems = getDueAtEditOptions(state);
    return dueAtEditItems?.equityAllocationMethod;
};

export const getFeesCapitalizedOverride = (state: StateTree): boolean | undefined => {
    const overrides = getDasOverrides(state);
    return overrides?.feesCapitalized;
};

export const getTaxesCapitalizedOverride = (state: StateTree): boolean | undefined => {
    const overrides = getDasOverrides(state);
    return overrides?.taxesCapitalized;
};

export const getDownPaymentAmountOverride = (state: StateTree): number | undefined => {
    const overrides = getDasOverrides(state);
    return overrides?.downPayment;
};

/**
 * Resolves the down payment, preferring the in-progress Due at Signing override and falling back
 * to the committed offer-redux value. Keeps the cash field reactive while the slideout recalculates.
 */
export const getDownPaymentAmountWithFallback = (state: StateTree): number => {
    const overrides = getDasOverrides(state);
    return overrides?.downPayment ?? getDownPayment(state) ?? 0;
};

export const isDasStateModified = createSelector(
    [getDasOverrides, (_state: StateTree, compareFlags: DasOverridesFlagsTypes) => compareFlags],
    (modifiedFlags, compareFlags): boolean => {
        const compareFields = Object.keys(DasOverridesCompare) as (keyof DasOverridesFlagsTypes)[];

        return compareFields.some((key) => {
            const defaultValue = compareFlags?.[key];
            const currentValue = modifiedFlags?.[key];
            return defaultValue !== currentValue;
        });
    }
);

export const getDasPayment = (state: StateTree): DasPayment => {
    const dueAtSigning = getDueAtEditOptions(state);
    return dueAtSigning.payment || {};
};

export const getDasErrors = (state: StateTree): GenericObject[] => {
    const dueAtSigning = getDueAtEditOptions(state);
    return dueAtSigning.errors || [];
};

export const getDasCapCostReduction = (state: StateTree): number => {
    const dasPayment = getDasPayment(state);
    const offerType = getCurrentOfferType(state);
    return (
        dasPayment.capCostReduction || (getCapCostReduction(state, offerType) ? Number(getCapCostReduction(state, offerType)) : 0)
    );
};

/**
 * Resolves the lease equity allocation method, preferring the in-progress Due at Signing override
 * (the "Equity Allocation Rule" dropdown) and falling back to the committed deal-screen value.
 */
export const getEquityAllocationMethodOverrideWithFallback = (state: StateTree): string | undefined => {
    const overrides = getDasOverrides(state);
    return overrides?.equityAllocationMethod ?? getEquityAllocationMethod(state);
};

export const getDasDefaultPayment = createSelector(
    [
        getDasPayment,
        getMonthlyPayment,
        getUpfrontTaxTotal,
        getUpfrontFeesTotal,
        getDasCapCostReduction,
        getSecurityDepositOverrideWithFallback,
        getNetTradeInAllowanceValue,
        getAppliedIncentivesTotalForNonDealerCash,
        getCashDueFromCustomer,
        getUpfrontAddonsTotal,
        getPriorLoanBalance,
        getPaidAtSigning,
        getDiaDueAtSigning
    ],
    (
        dasPayment,
        firstPaymentAmount,
        upfrontTaxTotal,
        upfrontFeesTotal,
        capCostReduction,
        secDeposit,
        netTrade,
        totalRebates,
        cashDueFromCustomer,
        upfrontAddonsTotal,
        priorLoanBalance,
        totalAmountDue,
        diaDueAtSigning
    ): DasPayment => {
        // @note dasPayment will only have value during the dueAtSigning calculation, meanwhile the value of montylyPayment, upfrontTaxTotal and upfrontFeesTotal will be used.
        return {
            firstPayment: dasPayment.firstPayment ?? firstPaymentAmount ?? 0,
            upfrontTaxTotal: dasPayment.upfrontTaxTotal ?? upfrontTaxTotal,
            upfrontFeesTotal: dasPayment.upfrontFeesTotal ?? upfrontFeesTotal,
            capCostReduction: dasPayment.capCostReduction ?? capCostReduction,
            secDeposit: dasPayment.secDeposit ?? secDeposit,
            netTrade: dasPayment.netTrade ?? netTrade,
            totalRebates: dasPayment.totalRebates ?? totalRebates,
            cashDueFromCustomer: dasPayment.cashDueFromCustomer ?? cashDueFromCustomer,
            upfrontAddonsTotal: dasPayment.upfrontAddonsTotal ?? upfrontAddonsTotal,
            priorLoanBalance: dasPayment.priorLoanBalance ?? priorLoanBalance,
            totalAmountDue: dasPayment.totalAmountDue ?? totalAmountDue,
            diaDueAtSigning: dasPayment.diaDueAtSigning ?? diaDueAtSigning
        };
    }
);

export const getInfoMessages = (state: StateTree): InfoMessage[] => {
    const dueAtEditItems = getDueAtEditOptions(state);
    return dueAtEditItems?.messages || [];
};

export const getDasFirstPayment = (state: StateTree): number => {
    const dasPayment = getDasPayment(state);
    return dasPayment.firstPayment || getFirstMonthPayment(state);
};

export const getDasCashDueFromCustomer = (state: StateTree): number => {
    return getDasDefaultPayment(state).cashDueFromCustomer as number;
};

export const eligibleTaxesCapped = (state: StateTree): boolean => {
    const areBaseTaxesCapped = isTaxCapped(state);
    const hasCapCostReductionTax = getCapCostReductionTax(state) > 0;
    const isCRTCapped = getCapReductionTaxCapitalizedIndicator(state);

    return hasCapCostReductionTax ? areBaseTaxesCapped && isCRTCapped : areBaseTaxesCapped;
};

export const getAllCapitalizedFallback = (allCapped: boolean, targetValue?: boolean): boolean | undefined => {
    return allCapped ? targetValue : targetValue || undefined;
};

export const getDefaultTaxesOverrides = (state: StateTree, overrides: DasOverrides): DasOverrides => {
    overrides.taxItems = setManualTaxes(state, overrides.taxesCapitalized);
    overrides.taxesCapitalized = isTermAllTaxCapitalized(state) || undefined;

    return overrides;
};

export const setDefaultFeesOverrides = (state: StateTree, overrides: DasOverrides): DasOverrides => {
    overrides.feesOverride = getFeesOverride(state);
    overrides.feesCapitalized = isTermAllFeesCapitalized(state) || undefined;

    return overrides;
};

export const getDasDefaultOverrides = (state: StateTree): DasOverrides => {
    const original = getDasOverrides(state) || {};
    const overrides: DasOverrides = { ...original };

    if (overrides.firstPaymentCapitalized == null) {
        // Live parity with Breakdown (selectedTerm → calculationDetails fallback).
        overrides.firstPaymentCapitalized = getFirstPaymentCapitalized(state) ?? getIsFirstPaymentCapitalized(state);
    }

    if (overrides.targetCashAmountPaid == null) {
        overrides.targetCashAmountPaid = getIsTargetOutOfPocket(state);
    }

    if (overrides.taxesCapitalized == null) {
        getDefaultTaxesOverrides(state, overrides);
    }

    if (overrides.feesCapitalized == null) {
        setDefaultFeesOverrides(state, overrides);
    }

    if (overrides.downPayment == null) {
        overrides.downPayment = getDownPayment(state);
    }

    if (overrides.equityAllocationMethod == null) {
        overrides.equityAllocationMethod = getEquityAllocationMethod(state);
    }

    return overrides;
};

export const setFeesOverrides = (state: StateTree, overrides: DasOverrides, targetValue?: boolean): DasOverrides => {
    const allCapped = isAllFeesCapitalized(state);
    const feesStatus = getAllCapitalizedFallback(allCapped, targetValue);

    overrides.feesOverride = feesStatus == null ? getFeesOverride(state) : updateFeesWithCapIndicator(state, feesStatus);
    overrides.feesCapitalized = feesStatus;

    return overrides;
};

export const setTaxesOverrides = (state: StateTree, overrides: DasOverrides, targetValue?: boolean): DasOverrides => {
    const allCapped = eligibleTaxesCapped(state);
    const taxesStatus = getAllCapitalizedFallback(allCapped, targetValue);

    overrides.taxesCapitalized = targetValue || taxesStatus;
    overrides.taxItems = setManualTaxes(state, overrides.taxesCapitalized);

    return overrides;
};

export const getDasOverridesUpdated = (
    state: StateTree,
    targetName: string,
    targetValue: boolean | number | string
): DasOverrides => {
    // Only populate defaults for fields that explicitly change tax/fee/payment capitalization
    // For simple value changes like downPayment or equityAllocationMethod, preserve existing overrides
    const shouldPopulateDefaults =
        targetName === DAS_OVERRIDES_FIELDS.FIRST_PAYMENT_CAPITALIZED ||
        targetName === DAS_OVERRIDES_FIELDS.FEES_CAPITALIZED ||
        targetName === DAS_OVERRIDES_FIELDS.TAXES_CAPITALIZED ||
        targetName === DAS_OVERRIDES_FIELDS.TARGET_CASH_AMOUNT_PAID;

    const original = getDasOverrides(state) || {};
    const overrides: DasOverrides = shouldPopulateDefaults ? getDasDefaultOverrides(state) : { ...original };

    switch (targetName) {
        case DAS_OVERRIDES_FIELDS.FIRST_PAYMENT_CAPITALIZED: {
            if (isBoolean(targetValue)) {
                overrides.firstPaymentCapitalized = targetValue;
            }
            break;
        }
        case DAS_OVERRIDES_FIELDS.FEES_CAPITALIZED: {
            if (isBoolean(targetValue)) {
                setFeesOverrides(state, overrides, targetValue);
            }
            break;
        }
        case DAS_OVERRIDES_FIELDS.TAXES_CAPITALIZED: {
            if (isBoolean(targetValue)) {
                setTaxesOverrides(state, overrides, targetValue);
            }
            break;
        }
        case DAS_OVERRIDES_FIELDS.TARGET_CASH_AMOUNT_PAID: {
            if (isBoolean(targetValue)) {
                if (targetValue) {
                    setFeesOverrides(state, overrides, targetValue);
                    setTaxesOverrides(state, overrides, targetValue);
                }
                overrides.targetCashAmountPaid = targetValue;

                // Checking the box puts the customer's cash first in the order; unchecking never strips it back out.
                if (
                    targetValue &&
                    featureToggleSelectors.isEquityAllocationOrderEnabled(state) &&
                    overrides.equityAllocationMethod
                ) {
                    overrides.equityAllocationMethod = offerReduxUtils.prependCashToEquityAllocationMethod(
                        overrides.equityAllocationMethod
                    );
                }
            }
            break;
        }
        case DAS_OVERRIDES_FIELDS.DOWNPAYMENT: {
            if (isNumber(targetValue)) {
                overrides.downPayment = targetValue;
            }
            break;
        }
        case DAS_OVERRIDES_FIELDS.EQUITY_ALLOCATION_METHOD: {
            if (typeof targetValue === 'string') {
                overrides.equityAllocationMethod = targetValue;
            }
            break;
        }
    }

    return overrides;
};

export const isBoolean = (value: boolean | number | string): value is boolean => {
    return typeof value === 'boolean';
};

export const isNumber = (value: boolean | number | string): value is number => {
    return typeof value === 'number';
};
